import React, { useState, Component } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, ShoppingCart, Package, Users, RotateCcw, Clock, DollarSign, ChevronDown, Star, Lightbulb, Check, Calendar, Zap, ArrowRight, ThumbsDown, ThumbsUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, ComposedChart, Area, Cell } from 'recharts';
import { Card } from '../components/ui/Card';
// --- Metric Types ---
type MetricType = 'revenue' | 'orders' | 'rating' | 'deliveryTime';
type ComparisonType = 'yesterday' | 'lastWeek' | 'lastMonth' | 'lastYear' | null;
// --- Data by Period and Metric ---
const METRIC_DATA = {
  '1D': {
    dateRange: 'Saturday, December 13, 2025',
    revenue: {
      current: '780K',
      currentRaw: 780,
      yesterday: {
        value: '965K',
        raw: 965,
        change: '-19.2%',
        diff: '-185K',
        trend: 'down' as const
      },
      lastWeek: {
        value: '711K',
        raw: 711,
        change: '+9.7%',
        diff: '+69K',
        trend: 'up' as const
      },
      lastMonth: {
        value: '718K',
        raw: 718,
        change: '+8.6%',
        diff: '+62K',
        trend: 'up' as const
      },
      lastYear: {
        value: '628K',
        raw: 628,
        change: '+24.2%',
        diff: '+152K',
        trend: 'up' as const
      },
      avg14Day: '758K',
      chartData: [{
        date: '00h',
        value: 0,
        yesterday: 0,
        lastWeek: 0,
        lastMonth: 0,
        lastYear: 0
      }, {
        date: '06h',
        value: 45,
        yesterday: 52,
        lastWeek: 38,
        lastMonth: 42,
        lastYear: 35
      }, {
        date: '12h',
        value: 320,
        yesterday: 410,
        lastWeek: 280,
        lastMonth: 295,
        lastYear: 245
      }, {
        date: '18h',
        value: 580,
        yesterday: 720,
        lastWeek: 520,
        lastMonth: 545,
        lastYear: 450
      }, {
        date: '24h',
        value: 780,
        yesterday: 965,
        lastWeek: 711,
        lastMonth: 718,
        lastYear: 628
      }]
    },
    orders: {
      current: '523',
      currentRaw: 523,
      yesterday: {
        value: '621',
        raw: 621,
        change: '-15.8%',
        diff: '-98',
        trend: 'down' as const
      },
      lastWeek: {
        value: '443',
        raw: 443,
        change: '+18.1%',
        diff: '+80',
        trend: 'up' as const
      },
      lastMonth: {
        value: '489',
        raw: 489,
        change: '+7.0%',
        diff: '+34',
        trend: 'up' as const
      },
      lastYear: {
        value: '412',
        raw: 412,
        change: '+26.9%',
        diff: '+111',
        trend: 'up' as const
      },
      avg14Day: '498',
      chartData: [{
        date: '00h',
        value: 0,
        yesterday: 0,
        lastWeek: 0,
        lastMonth: 0,
        lastYear: 0
      }, {
        date: '06h',
        value: 28,
        yesterday: 35,
        lastWeek: 22,
        lastMonth: 25,
        lastYear: 18
      }, {
        date: '12h',
        value: 215,
        yesterday: 280,
        lastWeek: 178,
        lastMonth: 195,
        lastYear: 152
      }, {
        date: '18h',
        value: 420,
        yesterday: 510,
        lastWeek: 356,
        lastMonth: 385,
        lastYear: 298
      }, {
        date: '24h',
        value: 523,
        yesterday: 621,
        lastWeek: 443,
        lastMonth: 489,
        lastYear: 412
      }]
    },
    rating: {
      current: '9.3',
      currentRaw: 9.3,
      yesterday: {
        value: '9.1',
        raw: 9.1,
        change: '+2.2%',
        diff: '+0.2',
        trend: 'up' as const
      },
      lastWeek: {
        value: '9.2',
        raw: 9.2,
        change: '+1.1%',
        diff: '+0.1',
        trend: 'up' as const
      },
      lastMonth: {
        value: '9.0',
        raw: 9.0,
        change: '+3.3%',
        diff: '+0.3',
        trend: 'up' as const
      },
      lastYear: {
        value: '8.8',
        raw: 8.8,
        change: '+5.7%',
        diff: '+0.5',
        trend: 'up' as const
      },
      avg14Day: '9.15',
      chartData: [{
        date: '00h',
        value: 0,
        yesterday: 0,
        lastWeek: 0,
        lastMonth: 0,
        lastYear: 0
      }, {
        date: '06h',
        value: 9.1,
        yesterday: 8.9,
        lastWeek: 9.0,
        lastMonth: 8.8,
        lastYear: 8.5
      }, {
        date: '12h',
        value: 9.2,
        yesterday: 9.0,
        lastWeek: 9.1,
        lastMonth: 8.9,
        lastYear: 8.7
      }, {
        date: '18h',
        value: 9.4,
        yesterday: 9.2,
        lastWeek: 9.3,
        lastMonth: 9.1,
        lastYear: 8.9
      }, {
        date: '24h',
        value: 9.3,
        yesterday: 9.1,
        lastWeek: 9.2,
        lastMonth: 9.0,
        lastYear: 8.8
      }]
    },
    deliveryTime: {
      current: '41.3',
      currentRaw: 41.3,
      yesterday: {
        value: '43.6',
        raw: 43.6,
        change: '-5.3%',
        diff: '-2.3',
        trend: 'up' as const
      },
      lastWeek: {
        value: '38.1',
        raw: 38.1,
        change: '+8.4%',
        diff: '+3.2',
        trend: 'down' as const
      },
      lastMonth: {
        value: '39.5',
        raw: 39.5,
        change: '+4.6%',
        diff: '+1.8',
        trend: 'down' as const
      },
      lastYear: {
        value: '45.2',
        raw: 45.2,
        change: '-8.6%',
        diff: '-3.9',
        trend: 'up' as const
      },
      avg14Day: '39.8',
      chartData: [{
        date: '00h',
        value: 0,
        yesterday: 0,
        lastWeek: 0,
        lastMonth: 0,
        lastYear: 0
      }, {
        date: '06h',
        value: 35,
        yesterday: 38,
        lastWeek: 32,
        lastMonth: 34,
        lastYear: 40
      }, {
        date: '12h',
        value: 42,
        yesterday: 45,
        lastWeek: 39,
        lastMonth: 40,
        lastYear: 47
      }, {
        date: '18h',
        value: 45,
        yesterday: 48,
        lastWeek: 41,
        lastMonth: 43,
        lastYear: 49
      }, {
        date: '24h',
        value: 41.3,
        yesterday: 43.6,
        lastWeek: 38.1,
        lastMonth: 39.5,
        lastYear: 45.2
      }]
    }
  },
  '7D': {
    dateRange: 'Dec 7 – Dec 13, 2025',
    revenue: {
      current: '5.12M',
      currentRaw: 5124,
      yesterday: {
        value: '4.56M',
        raw: 4560,
        change: '+12.3%',
        diff: '+564K',
        trend: 'up' as const
      },
      lastWeek: {
        value: '4.56M',
        raw: 4560,
        change: '+12.3%',
        diff: '+564K',
        trend: 'up' as const
      },
      lastMonth: {
        value: '4.71M',
        raw: 4710,
        change: '+8.8%',
        diff: '+414K',
        trend: 'up' as const
      },
      lastYear: {
        value: '4.12M',
        raw: 4120,
        change: '+24.4%',
        diff: '+1.0M',
        trend: 'up' as const
      },
      avg14Day: '4.89M',
      chartData: [{
        date: 'Dec 7',
        value: 680,
        yesterday: 620,
        lastWeek: 620,
        lastMonth: 645,
        lastYear: 550
      }, {
        date: 'Dec 8',
        value: 720,
        yesterday: 680,
        lastWeek: 680,
        lastMonth: 695,
        lastYear: 580
      }, {
        date: 'Dec 9',
        value: 695,
        yesterday: 650,
        lastWeek: 650,
        lastMonth: 670,
        lastYear: 565
      }, {
        date: 'Dec 10',
        value: 750,
        yesterday: 710,
        lastWeek: 710,
        lastMonth: 725,
        lastYear: 610
      }, {
        date: 'Dec 11',
        value: 810,
        yesterday: 720,
        lastWeek: 720,
        lastMonth: 745,
        lastYear: 645
      }, {
        date: 'Dec 12',
        value: 890,
        yesterday: 790,
        lastWeek: 790,
        lastMonth: 820,
        lastYear: 710
      }, {
        date: 'Dec 13',
        value: 780,
        yesterday: 711,
        lastWeek: 711,
        lastMonth: 735,
        lastYear: 628
      }]
    },
    orders: {
      current: '3,456',
      currentRaw: 3456,
      yesterday: {
        value: '2,993',
        raw: 2993,
        change: '+15.4%',
        diff: '+463',
        trend: 'up' as const
      },
      lastWeek: {
        value: '2,993',
        raw: 2993,
        change: '+15.4%',
        diff: '+463',
        trend: 'up' as const
      },
      lastMonth: {
        value: '3,105',
        raw: 3105,
        change: '+11.3%',
        diff: '+351',
        trend: 'up' as const
      },
      lastYear: {
        value: '2,780',
        raw: 2780,
        change: '+24.3%',
        diff: '+676',
        trend: 'up' as const
      },
      avg14Day: '3,289',
      chartData: [{
        date: 'Dec 7',
        value: 445,
        yesterday: 410,
        lastWeek: 410,
        lastMonth: 425,
        lastYear: 365
      }, {
        date: 'Dec 8',
        value: 478,
        yesterday: 445,
        lastWeek: 445,
        lastMonth: 458,
        lastYear: 390
      }, {
        date: 'Dec 9',
        value: 462,
        yesterday: 430,
        lastWeek: 430,
        lastMonth: 442,
        lastYear: 378
      }, {
        date: 'Dec 10',
        value: 502,
        yesterday: 468,
        lastWeek: 468,
        lastMonth: 485,
        lastYear: 412
      }, {
        date: 'Dec 11',
        value: 535,
        yesterday: 485,
        lastWeek: 485,
        lastMonth: 508,
        lastYear: 435
      }, {
        date: 'Dec 12',
        value: 580,
        yesterday: 512,
        lastWeek: 512,
        lastMonth: 538,
        lastYear: 468
      }, {
        date: 'Dec 13',
        value: 523,
        yesterday: 443,
        lastWeek: 443,
        lastMonth: 489,
        lastYear: 412
      }]
    },
    rating: {
      current: '9.2',
      currentRaw: 9.2,
      yesterday: {
        value: '9.1',
        raw: 9.1,
        change: '+1.1%',
        diff: '+0.1',
        trend: 'up' as const
      },
      lastWeek: {
        value: '9.1',
        raw: 9.1,
        change: '+1.1%',
        diff: '+0.1',
        trend: 'up' as const
      },
      lastMonth: {
        value: '9.0',
        raw: 9.0,
        change: '+2.2%',
        diff: '+0.2',
        trend: 'up' as const
      },
      lastYear: {
        value: '8.7',
        raw: 8.7,
        change: '+5.7%',
        diff: '+0.5',
        trend: 'up' as const
      },
      avg14Day: '9.1',
      chartData: [{
        date: 'Dec 7',
        value: 9.1,
        yesterday: 9.0,
        lastWeek: 9.0,
        lastMonth: 8.9,
        lastYear: 8.6
      }, {
        date: 'Dec 8',
        value: 9.2,
        yesterday: 9.1,
        lastWeek: 9.1,
        lastMonth: 9.0,
        lastYear: 8.7
      }, {
        date: 'Dec 9',
        value: 9.1,
        yesterday: 9.0,
        lastWeek: 9.0,
        lastMonth: 8.9,
        lastYear: 8.6
      }, {
        date: 'Dec 10',
        value: 9.3,
        yesterday: 9.2,
        lastWeek: 9.2,
        lastMonth: 9.1,
        lastYear: 8.8
      }, {
        date: 'Dec 11',
        value: 9.2,
        yesterday: 9.1,
        lastWeek: 9.1,
        lastMonth: 9.0,
        lastYear: 8.7
      }, {
        date: 'Dec 12',
        value: 9.3,
        yesterday: 9.2,
        lastWeek: 9.2,
        lastMonth: 9.1,
        lastYear: 8.8
      }, {
        date: 'Dec 13',
        value: 9.2,
        yesterday: 9.1,
        lastWeek: 9.1,
        lastMonth: 9.0,
        lastYear: 8.7
      }]
    },
    deliveryTime: {
      current: '38.7',
      currentRaw: 38.7,
      yesterday: {
        value: '40.2',
        raw: 40.2,
        change: '-3.7%',
        diff: '-1.5',
        trend: 'up' as const
      },
      lastWeek: {
        value: '40.2',
        raw: 40.2,
        change: '-3.7%',
        diff: '-1.5',
        trend: 'up' as const
      },
      lastMonth: {
        value: '39.8',
        raw: 39.8,
        change: '-2.8%',
        diff: '-1.1',
        trend: 'up' as const
      },
      lastYear: {
        value: '44.5',
        raw: 44.5,
        change: '-13.0%',
        diff: '-5.8',
        trend: 'up' as const
      },
      avg14Day: '39.5',
      chartData: [{
        date: 'Dec 7',
        value: 39,
        yesterday: 41,
        lastWeek: 41,
        lastMonth: 40,
        lastYear: 45
      }, {
        date: 'Dec 8',
        value: 38,
        yesterday: 40,
        lastWeek: 40,
        lastMonth: 39,
        lastYear: 44
      }, {
        date: 'Dec 9',
        value: 39,
        yesterday: 41,
        lastWeek: 41,
        lastMonth: 40,
        lastYear: 45
      }, {
        date: 'Dec 10',
        value: 37,
        yesterday: 39,
        lastWeek: 39,
        lastMonth: 38,
        lastYear: 43
      }, {
        date: 'Dec 11',
        value: 38,
        yesterday: 40,
        lastWeek: 40,
        lastMonth: 39,
        lastYear: 44
      }, {
        date: 'Dec 12',
        value: 39,
        yesterday: 41,
        lastWeek: 41,
        lastMonth: 40,
        lastYear: 45
      }, {
        date: 'Dec 13',
        value: 41,
        yesterday: 43,
        lastWeek: 43,
        lastMonth: 42,
        lastYear: 47
      }]
    }
  },
  '30D': {
    dateRange: 'Nov 14 – Dec 13, 2025',
    revenue: {
      current: '21.5M',
      currentRaw: 21457,
      yesterday: {
        value: '19.7M',
        raw: 19700,
        change: '+8.9%',
        diff: '+1.8M',
        trend: 'up' as const
      },
      lastWeek: {
        value: '19.7M',
        raw: 19700,
        change: '+8.9%',
        diff: '+1.8M',
        trend: 'up' as const
      },
      lastMonth: {
        value: '19.7M',
        raw: 19700,
        change: '+8.9%',
        diff: '+1.8M',
        trend: 'up' as const
      },
      lastYear: {
        value: '17.2M',
        raw: 17200,
        change: '+24.7%',
        diff: '+4.3M',
        trend: 'up' as const
      },
      avg14Day: '20.8M',
      chartData: [{
        date: 'Nov 14',
        value: 650,
        yesterday: 580,
        lastWeek: 580,
        lastMonth: 580,
        lastYear: 520
      }, {
        date: 'Nov 21',
        value: 680,
        yesterday: 610,
        lastWeek: 610,
        lastMonth: 610,
        lastYear: 545
      }, {
        date: 'Nov 28',
        value: 710,
        yesterday: 640,
        lastWeek: 640,
        lastMonth: 640,
        lastYear: 570
      }, {
        date: 'Dec 5',
        value: 740,
        yesterday: 680,
        lastWeek: 680,
        lastMonth: 680,
        lastYear: 605
      }, {
        date: 'Dec 13',
        value: 780,
        yesterday: 711,
        lastWeek: 711,
        lastMonth: 711,
        lastYear: 628
      }]
    },
    orders: {
      current: '14,523',
      currentRaw: 14523,
      yesterday: {
        value: '13,058',
        raw: 13058,
        change: '+11.2%',
        diff: '+1,465',
        trend: 'up' as const
      },
      lastWeek: {
        value: '13,058',
        raw: 13058,
        change: '+11.2%',
        diff: '+1,465',
        trend: 'up' as const
      },
      lastMonth: {
        value: '13,058',
        raw: 13058,
        change: '+11.2%',
        diff: '+1,465',
        trend: 'up' as const
      },
      lastYear: {
        value: '11,680',
        raw: 11680,
        change: '+24.3%',
        diff: '+2,843',
        trend: 'up' as const
      },
      avg14Day: '13,890',
      chartData: [{
        date: 'Nov 14',
        value: 2850,
        yesterday: 2560,
        lastWeek: 2560,
        lastMonth: 2560,
        lastYear: 2180
      }, {
        date: 'Nov 21',
        value: 2920,
        yesterday: 2640,
        lastWeek: 2640,
        lastMonth: 2640,
        lastYear: 2250
      }, {
        date: 'Nov 28',
        value: 2980,
        yesterday: 2710,
        lastWeek: 2710,
        lastMonth: 2710,
        lastYear: 2310
      }, {
        date: 'Dec 5',
        value: 3050,
        yesterday: 2780,
        lastWeek: 2780,
        lastMonth: 2780,
        lastYear: 2380
      }, {
        date: 'Dec 13',
        value: 3180,
        yesterday: 2890,
        lastWeek: 2890,
        lastMonth: 2890,
        lastYear: 2480
      }]
    },
    rating: {
      current: '9.1',
      currentRaw: 9.1,
      yesterday: {
        value: '9.0',
        raw: 9.0,
        change: '+1.1%',
        diff: '+0.1',
        trend: 'up' as const
      },
      lastWeek: {
        value: '9.0',
        raw: 9.0,
        change: '+1.1%',
        diff: '+0.1',
        trend: 'up' as const
      },
      lastMonth: {
        value: '9.0',
        raw: 9.0,
        change: '+1.1%',
        diff: '+0.1',
        trend: 'up' as const
      },
      lastYear: {
        value: '8.6',
        raw: 8.6,
        change: '+5.8%',
        diff: '+0.5',
        trend: 'up' as const
      },
      avg14Day: '9.0',
      chartData: [{
        date: 'Nov 14',
        value: 9.0,
        yesterday: 8.9,
        lastWeek: 8.9,
        lastMonth: 8.9,
        lastYear: 8.5
      }, {
        date: 'Nov 21',
        value: 9.1,
        yesterday: 9.0,
        lastWeek: 9.0,
        lastMonth: 9.0,
        lastYear: 8.6
      }, {
        date: 'Nov 28',
        value: 9.0,
        yesterday: 8.9,
        lastWeek: 8.9,
        lastMonth: 8.9,
        lastYear: 8.5
      }, {
        date: 'Dec 5',
        value: 9.2,
        yesterday: 9.1,
        lastWeek: 9.1,
        lastMonth: 9.1,
        lastYear: 8.7
      }, {
        date: 'Dec 13',
        value: 9.1,
        yesterday: 9.0,
        lastWeek: 9.0,
        lastMonth: 9.0,
        lastYear: 8.6
      }]
    },
    deliveryTime: {
      current: '39.2',
      currentRaw: 39.2,
      yesterday: {
        value: '41.5',
        raw: 41.5,
        change: '-5.5%',
        diff: '-2.3',
        trend: 'up' as const
      },
      lastWeek: {
        value: '41.5',
        raw: 41.5,
        change: '-5.5%',
        diff: '-2.3',
        trend: 'up' as const
      },
      lastMonth: {
        value: '41.5',
        raw: 41.5,
        change: '-5.5%',
        diff: '-2.3',
        trend: 'up' as const
      },
      lastYear: {
        value: '46.8',
        raw: 46.8,
        change: '-16.2%',
        diff: '-7.6',
        trend: 'up' as const
      },
      avg14Day: '40.2',
      chartData: [{
        date: 'Nov 14',
        value: 40,
        yesterday: 42,
        lastWeek: 42,
        lastMonth: 42,
        lastYear: 48
      }, {
        date: 'Nov 21',
        value: 39,
        yesterday: 41,
        lastWeek: 41,
        lastMonth: 41,
        lastYear: 47
      }, {
        date: 'Nov 28',
        value: 40,
        yesterday: 42,
        lastWeek: 42,
        lastMonth: 42,
        lastYear: 48
      }, {
        date: 'Dec 5',
        value: 38,
        yesterday: 40,
        lastWeek: 40,
        lastMonth: 40,
        lastYear: 45
      }, {
        date: 'Dec 13',
        value: 39,
        yesterday: 41,
        lastWeek: 41,
        lastMonth: 41,
        lastYear: 46
      }]
    }
  }
};
const PERIOD_DATA = {
  '1D': {
    summary: {
      revenue: {
        value: '780,139',
        change: '+9.7%',
        changeLabel: 'vs last Sat',
        trend: 'up' as const
      },
      profit: {
        value: '652,519',
        subValue: '(83.6%)',
        change: '+13.6%',
        changeLabel: 'margin',
        trend: 'up' as const
      },
      orders: {
        value: '523',
        subValue: 'transactions',
        change: '+18.1%',
        changeLabel: 'vs last Sat',
        trend: 'up' as const
      },
      rating: {
        value: '9.3',
        subValue: 'out of 10',
        change: '+1.1%',
        changeLabel: 'vs last Sat',
        trend: 'up' as const
      }
    },
    secondary: {
      avgBasket: '1,492',
      itemsPerOrder: '1.83',
      newCustomers: {
        value: '142',
        percent: '32.0%'
      },
      returning: {
        value: '302',
        percent: '68%'
      },
      delivery: {
        value: '41.3',
        warning: true
      },
      margin: {
        value: '83.6%',
        subValue: '653K'
      }
    },
    channels: [{
      name: 'Wolt',
      revenue: 639,
      percent: 72.0,
      color: '#3B82F6'
    }, {
      name: 'Dining In',
      revenue: 118,
      percent: 15.0,
      color: '#22C55E'
    }, {
      name: 'Collina.rs',
      revenue: 62,
      percent: 8.0,
      color: '#8B5CF6'
    }, {
      name: 'Food Truck',
      revenue: 42,
      percent: 5.0,
      color: '#F59E0B'
    }],
    locations: [{
      name: 'Vračar',
      revenue: 429000,
      orders: 288,
      growth: 13.8,
      color: '#8B5CF6'
    }, {
      name: 'Banovo Brdo',
      revenue: 164000,
      orders: 110,
      growth: 4,
      color: '#F59E0B'
    }, {
      name: 'Novi Beograd',
      revenue: 156000,
      orders: 105,
      growth: 21.8,
      color: '#22C55E'
    }, {
      name: 'Kuhinjica',
      revenue: 23000,
      orders: 16,
      growth: 9.7,
      color: '#6366F1'
    }, {
      name: 'Pekarica',
      revenue: 8000,
      orders: 5,
      growth: 9.7,
      color: '#EC4899'
    }]
  },
  '7D': {
    summary: {
      revenue: {
        value: '5,124,892',
        change: '+12.3%',
        changeLabel: 'vs prev week',
        trend: 'up' as const
      },
      profit: {
        value: '4,284,330',
        subValue: '(83.6%)',
        change: '+8.2%',
        changeLabel: 'margin',
        trend: 'up' as const
      },
      orders: {
        value: '3,456',
        subValue: 'transactions',
        change: '+15.4%',
        changeLabel: 'vs prev week',
        trend: 'up' as const
      },
      rating: {
        value: '9.2',
        subValue: 'out of 10',
        change: '+0.3%',
        changeLabel: 'vs prev week',
        trend: 'up' as const
      }
    },
    secondary: {
      avgBasket: '1,483',
      itemsPerOrder: '1.79',
      newCustomers: {
        value: '312',
        percent: '9.0%'
      },
      returning: {
        value: '2,890',
        percent: '84%'
      },
      delivery: {
        value: '38.7',
        warning: false
      },
      margin: {
        value: '83.6%',
        subValue: '4.28M'
      }
    },
    channels: [{
      name: 'Wolt',
      revenue: 3690,
      percent: 72.0,
      color: '#3B82F6'
    }, {
      name: 'Dining In',
      revenue: 820,
      percent: 16.0,
      color: '#22C55E'
    }, {
      name: 'Collina.rs',
      revenue: 410,
      percent: 8.0,
      color: '#8B5CF6'
    }, {
      name: 'Food Truck',
      revenue: 205,
      percent: 4.0,
      color: '#F59E0B'
    }],
    locations: [{
      name: 'Vračar',
      revenue: 2050000,
      orders: 1380,
      growth: 14.2,
      color: '#8B5CF6'
    }, {
      name: 'Banovo Brdo',
      revenue: 1180000,
      orders: 795,
      growth: 8.5,
      color: '#F59E0B'
    }, {
      name: 'Novi Beograd',
      revenue: 1025000,
      orders: 690,
      growth: 18.3,
      color: '#22C55E'
    }, {
      name: 'Kuhinjica',
      revenue: 156000,
      orders: 105,
      growth: 5.2,
      color: '#6366F1'
    }, {
      name: 'Pekarica',
      revenue: 52000,
      orders: 35,
      growth: 3.1,
      color: '#EC4899'
    }]
  },
  '30D': {
    summary: {
      revenue: {
        value: '21,456,780',
        change: '+8.7%',
        changeLabel: 'vs prev month',
        trend: 'up' as const
      },
      profit: {
        value: '17,938,267',
        subValue: '(83.6%)',
        change: '+6.4%',
        changeLabel: 'margin',
        trend: 'up' as const
      },
      orders: {
        value: '14,523',
        subValue: 'transactions',
        change: '+11.2%',
        changeLabel: 'vs prev month',
        trend: 'up' as const
      },
      rating: {
        value: '9.1',
        subValue: 'out of 10',
        change: '+0.5%',
        changeLabel: 'vs prev month',
        trend: 'up' as const
      }
    },
    secondary: {
      avgBasket: '1,478',
      itemsPerOrder: '1.81',
      newCustomers: {
        value: '1,245',
        percent: '8.6%'
      },
      returning: {
        value: '12,456',
        percent: '86%'
      },
      delivery: {
        value: '39.2',
        warning: false
      },
      margin: {
        value: '83.6%',
        subValue: '17.9M'
      }
    },
    channels: [{
      name: 'Wolt',
      revenue: 15020,
      percent: 70.0,
      color: '#3B82F6'
    }, {
      name: 'Dining In',
      revenue: 3648,
      percent: 17.0,
      color: '#22C55E'
    }, {
      name: 'Collina.rs',
      revenue: 1932,
      percent: 9.0,
      color: '#8B5CF6'
    }, {
      name: 'Food Truck',
      revenue: 858,
      percent: 4.0,
      color: '#F59E0B'
    }],
    locations: [{
      name: 'Vračar',
      revenue: 8582000,
      orders: 5810,
      growth: 10.5,
      color: '#8B5CF6'
    }, {
      name: 'Banovo Brdo',
      revenue: 4934000,
      orders: 3340,
      growth: 6.8,
      color: '#F59E0B'
    }, {
      name: 'Novi Beograd',
      revenue: 4291000,
      orders: 2905,
      growth: 12.1,
      color: '#22C55E'
    }, {
      name: 'Kuhinjica',
      revenue: 643000,
      orders: 435,
      growth: 4.3,
      color: '#6366F1'
    }, {
      name: 'Pekarica',
      revenue: 214000,
      orders: 145,
      growth: 2.8,
      color: '#EC4899'
    }]
  }
};
const METRIC_CONFIG = {
  revenue: {
    label: 'Revenue',
    unit: 'RSD',
    format: (v: number) => `${v}K`
  },
  orders: {
    label: 'Number of Orders',
    unit: 'orders',
    format: (v: number) => v.toString()
  },
  rating: {
    label: 'Avg Rating',
    unit: '/ 10',
    format: (v: number) => v.toFixed(1)
  },
  deliveryTime: {
    label: 'Avg Delivery Time',
    unit: 'min',
    format: (v: number) => `${v.toFixed(1)} min`
  }
};
const topProducts = [{
  id: 1,
  name: 'BIG Double Collina Smash + pomfrit + Pepsi',
  sold: 261,
  rating: 9.2,
  revenue: '234K',
  profit: '201K',
  margin: '86%'
}, {
  id: 2,
  name: '!!! NOVO !!! Supreme Collina Triple Smash +Bac',
  sold: 77,
  rating: 10,
  revenue: '92K',
  profit: '79K',
  margin: '86%'
}, {
  id: 3,
  name: '2X Collina burger+ pomfrit + 2x Pepsi 0.33💙',
  sold: 58,
  rating: 9,
  revenue: '89K',
  profit: '76K',
  margin: '86%'
}, {
  id: 4,
  name: '!!! NOVO !!! BIG DoubleCHEESE COMBO– duplo si',
  sold: 80,
  rating: 9.8,
  revenue: '77K',
  profit: '66K',
  margin: '86%'
}, {
  id: 5,
  name: 'Palačinka nutela',
  sold: 40,
  rating: 10,
  revenue: '23K',
  profit: '19K',
  margin: '80.4%'
}];
const pnlData = [{
  label: 'BRUTTO REVENUE',
  value: '780,139',
  percent: '100%',
  isTotal: true,
  color: 'text-white'
}, {
  label: 'Food Cost (COGS)',
  value: '-127,943',
  percent: '16.4%',
  isTotal: false,
  color: 'text-accent-error'
}, {
  label: '= GROSS PROFIT',
  value: '652,196',
  percent: '83.6%',
  isTotal: true,
  color: 'text-accent-success'
}, {
  label: 'Wolt Fee',
  value: '-85,815',
  percent: '11%',
  isTotal: false,
  color: 'text-blue-400'
}, {
  label: 'Staff *',
  value: '-80,000',
  percent: '10.3%',
  isTotal: false,
  color: 'text-yellow-400'
}, {
  label: 'Rent *',
  value: '-25,000',
  percent: '3.2%',
  isTotal: false,
  color: 'text-orange-400'
}, {
  label: 'Utilities *',
  value: '-12,000',
  percent: '1.5%',
  isTotal: false,
  color: 'text-yellow-200'
}, {
  label: 'Packaging *',
  value: '-14,000',
  percent: '1.8%',
  isTotal: false,
  color: 'text-orange-200'
}, {
  label: 'Other (admin, insurance) *',
  value: '-10,000',
  percent: '1.3%',
  isTotal: false,
  color: 'text-gray-400'
}, {
  label: '= EBITDA',
  value: '425,381',
  percent: '54.5%',
  isTotal: true,
  color: 'text-accent-warning'
}, {
  label: 'Taxes (~15%) *',
  value: '-63,807',
  percent: '8.2%',
  isTotal: false,
  color: 'text-gray-400'
}, {
  label: '= NET PROFIT',
  value: '361,574',
  percent: '46.3%',
  isTotal: true,
  color: 'text-accent-success'
}];
// --- New Data for Additional Sections ---
const hourlyAnalysisData = [{
  hour: '8h',
  orders: 5,
  prep: 10,
  delivery: 35
}, {
  hour: '10h',
  orders: 15,
  prep: 12,
  delivery: 38
}, {
  hour: '12h',
  orders: 45,
  prep: 15,
  delivery: 42
}, {
  hour: '14h',
  orders: 55,
  prep: 18,
  delivery: 45
}, {
  hour: '16h',
  orders: 48,
  prep: 16,
  delivery: 43
}, {
  hour: '18h',
  orders: 60,
  prep: 20,
  delivery: 48
}, {
  hour: '20h',
  orders: 50,
  prep: 18,
  delivery: 45
}, {
  hour: '22h',
  orders: 30,
  prep: 15,
  delivery: 40
}];
const dayOfWeekData = [{
  day: 'Mon',
  revenue: 693,
  orders: 477
}, {
  day: 'Tue',
  revenue: 682,
  orders: 467
}, {
  day: 'Wed',
  revenue: 772,
  orders: 512
}, {
  day: 'Thu',
  revenue: 839,
  orders: 555
}, {
  day: 'Fri',
  revenue: 963,
  orders: 655
}, {
  day: 'Sat',
  revenue: 780,
  orders: 523,
  isToday: true
}, {
  day: 'Sun',
  revenue: 715,
  orders: 483
}];
const criticalHours = [{
  hour: '16:00',
  orders: 48,
  revenue: '73K',
  delivery: '43m',
  rating: 9.3
}, {
  hour: '14:00',
  orders: 47,
  revenue: '70K',
  delivery: '45m',
  rating: 9.3
}, {
  hour: '21:00',
  orders: 46,
  revenue: '75K',
  delivery: '44m',
  rating: 9.3
}];
const prepTimeByVenue = [{
  name: 'Vračar',
  time: '13m',
  orders: 215,
  status: 'ok'
}, {
  name: 'Banovo Brdo',
  time: '13m',
  orders: 94,
  status: 'ok'
}, {
  name: 'Novi Beograd',
  time: '14m',
  orders: 57,
  status: 'ok'
}, {
  name: 'SMASH Vračar',
  time: '12m',
  orders: 80,
  status: 'ok'
}, {
  name: 'SMASH NB',
  time: '14m',
  orders: 35,
  status: 'ok'
}, {
  name: 'SMASH BB',
  time: '13m',
  orders: 20,
  status: 'ok'
}];
const prepTimeByHour = [{
  hour: '10h',
  good: 80,
  warn: 15,
  crit: 5
}, {
  hour: '12h',
  good: 70,
  warn: 20,
  crit: 10
}, {
  hour: '14h',
  good: 60,
  warn: 30,
  crit: 10
}, {
  hour: '16h',
  good: 75,
  warn: 20,
  crit: 5
}, {
  hour: '18h',
  good: 65,
  warn: 25,
  crit: 10
}, {
  hour: '20h',
  good: 70,
  warn: 20,
  crit: 10
}, {
  hour: '22h',
  good: 85,
  warn: 10,
  crit: 5
}];
const deliveryByVenue = [{
  name: 'Vračar',
  time: '40.3m',
  percent: 40,
  status: 'warn'
}, {
  name: 'Banovo Brdo',
  time: '39.7m',
  percent: 35,
  status: 'warn'
}, {
  name: 'Novi Beograd',
  time: '41.9m',
  percent: 36,
  status: 'warn'
}, {
  name: 'SMASH Vračar',
  time: '40.5m',
  percent: 36,
  status: 'warn'
}, {
  name: 'SMASH NB',
  time: '50.9m',
  percent: 14,
  status: 'crit'
}, {
  name: 'SMASH BB',
  time: '45.3m',
  percent: 30,
  status: 'warn'
}];
const categoryPerformance = [{
  name: 'Sides',
  percent: 74,
  revenue: '577K',
  margin: 86.1,
  change: 0
}, {
  name: 'Pancakes',
  percent: 10.9,
  revenue: '85K',
  margin: 80.4,
  change: 0
}, {
  name: 'Burgers',
  percent: 10.1,
  revenue: '78K',
  margin: 71.5,
  change: 0
}, {
  name: 'Cooked Meals',
  percent: 2.3,
  revenue: '18K',
  margin: 82.5,
  change: 0
}, {
  name: 'Bakery',
  percent: 1.6,
  revenue: '12K',
  margin: 79.3,
  change: 0
}, {
  name: 'Drinks',
  percent: 0.9,
  revenue: '7K',
  margin: 74.6,
  change: 0
}, {
  name: 'Gyros',
  percent: 0.3,
  revenue: '2K',
  margin: 75.0,
  change: 0
}];
const vipCustomers = [{
  rank: 1,
  id: 'VIP-4821',
  orders: 18,
  spent: '32K'
}, {
  rank: 2,
  id: 'VIP-3156',
  orders: 15,
  spent: '28K'
}, {
  rank: 3,
  id: 'VIP-7293',
  orders: 14,
  spent: '24K'
}, {
  rank: 4,
  id: 'VIP-5847',
  orders: 12,
  spent: '21K'
}, {
  rank: 5,
  id: 'VIP-9124',
  orders: 11,
  spent: '18K'
}];
const ratingDist = [{
  label: '10/10',
  count: 28,
  color: 'bg-accent-success'
}, {
  label: '9/10',
  count: 19,
  color: 'bg-accent-success'
}, {
  label: '8/10',
  count: 9,
  color: 'bg-accent-success'
}, {
  label: '≤7/10',
  count: 6,
  color: 'bg-accent-error'
}];
const keyInsights = [{
  icon: Star,
  label: 'Review Score',
  text: '9.3/10 average - up 1.1% vs last week'
}, {
  icon: DollarSign,
  label: 'Profit Margin',
  text: '83.6% average margin generating 653K RSD profit'
}, {
  icon: Package,
  label: 'Order Volume',
  text: '523 orders today, +18.1% vs last Saturday'
}, {
  icon: Clock,
  label: 'Delivery Time',
  text: '41.3 min average. Above target - needs attention'
}, {
  icon: ShoppingCart,
  label: 'Basket Size',
  text: '1,492 RSD average, 1.83 items per order'
}];
const recommendedActions = [{
  priority: 'HIGH',
  color: 'text-accent-error bg-accent-error/10',
  action: 'Monitor delivery times during peak hours',
  detail: '41.3 min avg delivery'
}, {
  priority: 'HIGH',
  color: 'text-accent-error bg-accent-error/10',
  action: 'Review venues with low ratings',
  detail: 'Quality consistency check'
}, {
  priority: 'MEDIUM',
  color: 'text-accent-warning bg-accent-warning/10',
  action: 'Promote high-margin categories',
  detail: 'Cooked meals at 82.5% margin'
}, {
  priority: 'MEDIUM',
  color: 'text-accent-warning bg-accent-warning/10',
  action: 'Upsell sides with burger orders',
  detail: '86% margin on pomfrit/sides'
}, {
  priority: 'LOW',
  color: 'text-accent-success bg-accent-success/10',
  action: "Prepare for tomorrow's forecast",
  detail: '483 orders expected'
}];
// --- Helper Components ---
function MetricCard({
  label,
  value,
  subValue,
  trend,
  trendValue,
  trendLabel,
  highlight = false
}: {
  label: string;
  value: string;
  subValue?: string;
  trend?: 'up' | 'down';
  trendValue?: string;
  trendLabel?: string;
  highlight?: boolean;
}) {
  return <Card className={`flex flex-col justify-between h-full ${highlight ? 'bg-background-elevated' : ''}`} data-id="element-191">
      <div data-id="element-192">
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2" data-id="element-193">
          {label}
        </h3>
        <div className="text-3xl font-bold text-text-primary mb-1" data-id="element-194">{value}</div>
        {subValue && <div className="text-sm text-text-secondary mb-3" data-id="element-195">{subValue}</div>}
      </div>
      {trendValue && <div className="flex items-center text-sm" data-id="element-196">
          <span className={`font-medium flex items-center ${trend === 'up' ? 'text-accent-success' : 'text-accent-error'}`} data-id="element-197">
            <span className="mr-1" data-id="element-198">●</span> {trendValue}
          </span>
          {trendLabel && <span className="text-text-secondary ml-1.5" data-id="element-199">{trendLabel}</span>}
        </div>}
    </Card>;
}
function SmallMetricCard({
  icon: Icon,
  label,
  value,
  subValue,
  warning = false,
  alert = false
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  subValue?: string;
  warning?: boolean;
  alert?: boolean;
}) {
  return <Card className="flex flex-col h-full relative overflow-hidden" data-id="element-200">
      <div className="flex justify-between items-start mb-4" data-id="element-201">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${warning ? 'bg-accent-warning/10 text-accent-warning' : 'bg-white/5 text-text-secondary'}`} data-id="element-202">
          <Icon size={20} data-id="element-203" />
        </div>
        {alert && <AlertTriangle size={16} className="text-accent-warning" data-id="element-204" />}
      </div>
      <div data-id="element-205">
        <h3 className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-1" data-id="element-206">
          {label}
        </h3>
        <div className="text-2xl font-bold text-text-primary flex items-baseline gap-1" data-id="element-207">
          {value}
        </div>
        {subValue && <div className="text-xs text-text-secondary mt-1" data-id="element-208">{subValue}</div>}
      </div>
    </Card>;
}
export function AnalyticsDashboard() {
  const [period, setPeriod] = useState<'1D' | '7D' | '30D'>('1D');
  const [metric, setMetric] = useState<MetricType>('revenue');
  const [comparison, setComparison] = useState<ComparisonType>('lastWeek');
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);
  const currentPeriodData = PERIOD_DATA[period];
  const currentMetricData = METRIC_DATA[period][metric];
  const metricConfig = METRIC_CONFIG[metric];
  // Get comparison data based on selection
  const getComparisonData = () => {
    if (!comparison) return null;
    return currentMetricData[comparison];
  };
  const comparisonData = getComparisonData();
  // Get chart data key based on comparison
  const getChartComparisonKey = () => {
    if (!comparison) return null;
    return comparison;
  };
  return <div className="space-y-6 animate-in fade-in duration-500 pb-10" data-id="element-209">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-210">
        <div data-id="element-211">
          <h1 className="text-3xl font-bold text-accent-primary flex items-center gap-3" data-id="element-212">
            Collina Analytics
            <span className="bg-accent-warning/10 p-1.5 rounded-md" data-id="element-213">
              <AlertTriangle className="w-5 h-5 text-accent-warning" data-id="element-214" />
            </span>
          </h1>
          <p className="text-text-secondary mt-1" data-id="element-215">
            {METRIC_DATA[period].dateRange}
          </p>
        </div>

        <div className="flex bg-background-card rounded-lg p-1 border border-white/5" data-id="element-216">
          {(['1D', '7D', '30D'] as const).map(range => <button key={range} onClick={() => setPeriod(range)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${period === range ? 'bg-accent-primary text-white' : 'text-text-secondary hover:text-white hover:bg-white/5'}`} data-id="element-217">
              {range}
            </button>)}
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-218">
        <MetricCard label="REVENUE" value={currentPeriodData.summary.revenue.value} subValue="RSD" trend={currentPeriodData.summary.revenue.trend} trendValue={currentPeriodData.summary.revenue.change} trendLabel={currentPeriodData.summary.revenue.changeLabel} data-id="element-219" />
        <MetricCard label="PROFIT" value={currentPeriodData.summary.profit.value} subValue={`RSD ${currentPeriodData.summary.profit.subValue}`} trend={currentPeriodData.summary.profit.trend} trendValue={currentPeriodData.summary.profit.change} trendLabel={currentPeriodData.summary.profit.changeLabel} highlight data-id="element-220" />
        <MetricCard label="ORDERS" value={currentPeriodData.summary.orders.value} subValue={currentPeriodData.summary.orders.subValue} trend={currentPeriodData.summary.orders.trend} trendValue={currentPeriodData.summary.orders.change} trendLabel={currentPeriodData.summary.orders.changeLabel} data-id="element-221" />
        <MetricCard label="AVG RATING" value={currentPeriodData.summary.rating.value} subValue={currentPeriodData.summary.rating.subValue} trend={currentPeriodData.summary.rating.trend} trendValue={currentPeriodData.summary.rating.change} trendLabel={currentPeriodData.summary.rating.changeLabel} data-id="element-222" />
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-223">
        <SmallMetricCard icon={ShoppingCart} label="AVG BASKET" value={<>
              {currentPeriodData.secondary.avgBasket}{' '}
              <span className="text-sm font-normal text-text-secondary" data-id="element-225">
                RSD
              </span>
            </>} data-id="element-224" />
        <SmallMetricCard icon={Package} label="ITEMS/ORDER" value={currentPeriodData.secondary.itemsPerOrder} data-id="element-226" />
        <SmallMetricCard icon={Users} label="NEW CUST" value={currentPeriodData.secondary.newCustomers.value} subValue={currentPeriodData.secondary.newCustomers.percent} data-id="element-227" />
        <SmallMetricCard icon={RotateCcw} label="RETURNING" value={currentPeriodData.secondary.returning.value} subValue={currentPeriodData.secondary.returning.percent} data-id="element-228" />
        <SmallMetricCard icon={Clock} label="DELIVERY" value={<>
              {currentPeriodData.secondary.delivery.value}{' '}
              <span className="text-sm font-normal text-text-secondary" data-id="element-230">
                min
              </span>
            </>} warning={currentPeriodData.secondary.delivery.warning} alert={currentPeriodData.secondary.delivery.warning} data-id="element-229" />
        <SmallMetricCard icon={DollarSign} label="MARGIN" value={currentPeriodData.secondary.margin.value} subValue={currentPeriodData.secondary.margin.subValue} data-id="element-231" />
      </div>

      {/* Trend Overview */}
      <Card className="p-6" data-id="element-232">
        <div className="flex items-center justify-between mb-6" data-id="element-233">
          <div className="flex items-center gap-3" data-id="element-234">
            <div className="p-2 bg-white/5 rounded-lg" data-id="element-235">
              <TrendingUp className="text-accent-primary" data-id="element-236" />
            </div>
            <h2 className="text-lg font-bold" data-id="element-237">Trend Overview</h2>
          </div>

          {/* Metric Dropdown */}
          <div className="relative" data-id="element-238">
            <button onClick={() => setShowMetricDropdown(!showMetricDropdown)} className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg text-sm text-text-secondary hover:text-white transition-colors" data-id="element-239">
              <span className="w-2 h-2 rounded-full bg-accent-primary" data-id="element-240"></span>
              {metricConfig.label}
              <ChevronDown size={14} data-id="element-241" />
            </button>

            {showMetricDropdown && <div className="absolute right-0 top-full mt-2 w-56 bg-background-elevated border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden" data-id="element-242">
                {(Object.keys(METRIC_CONFIG) as MetricType[]).map(m => <button key={m} onClick={() => {
              setMetric(m);
              setShowMetricDropdown(false);
            }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-white/5 transition-colors flex items-center justify-between ${metric === m ? 'text-accent-primary bg-white/5' : 'text-text-secondary'}`} data-id="element-243">
                    <span data-id="element-244">{METRIC_CONFIG[m].label}</span>
                    {metric === m && <Check size={14} data-id="element-245" />}
                  </button>)}
              </div>}
          </div>
        </div>

        {/* Dynamic Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-246">
          {/* Card 1: Current (always shown) */}
          <div className="p-4 bg-white/5 rounded-lg" data-id="element-247">
            <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-248">
              Current
            </div>
            <div className="text-2xl font-bold" data-id="element-249">
              {currentMetricData.current}
            </div>
            <div className="text-xs text-text-muted" data-id="element-250">{metricConfig.unit}</div>
          </div>

          {/* Card 2: Comparison value or default */}
          {comparison ? <div className="p-4 bg-accent-primary/10 border border-accent-primary/20 rounded-lg" data-id="element-251">
              <div className="text-xs text-accent-primary uppercase mb-1" data-id="element-252">
                {comparison === 'yesterday' ? 'Yesterday' : comparison === 'lastWeek' ? 'Last Week' : comparison === 'lastMonth' ? 'Last Month' : 'Last Year'}
              </div>
              <div className="text-2xl font-bold text-accent-primary" data-id="element-253">
                {comparisonData?.value}
              </div>
              <div className="text-xs text-accent-primary/80" data-id="element-254">
                {comparison === 'yesterday' ? 'Dec 12' : comparison === 'lastWeek' ? 'Dec 6' : comparison === 'lastMonth' ? 'Nov 13' : 'Dec 13, 2024'}
              </div>
            </div> : <div className="p-4 bg-white/5 rounded-lg" data-id="element-255">
              <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-256">
                Vs Yesterday
              </div>
              <div className={`text-2xl font-bold ${currentMetricData.yesterday.trend === 'up' ? 'text-accent-success' : 'text-accent-error'}`} data-id="element-257">
                {currentMetricData.yesterday.change}
              </div>
              <div className="text-xs text-text-muted" data-id="element-258">
                {currentMetricData.yesterday.value} prev
              </div>
            </div>}

          {/* Card 3: Change or default */}
          {comparison ? <div className="p-4 bg-white/5 rounded-lg" data-id="element-259">
              <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-260">
                Change
              </div>
              <div className={`text-2xl font-bold ${comparisonData?.trend === 'up' ? 'text-accent-success' : 'text-accent-error'}`} data-id="element-261">
                {comparisonData?.change}
              </div>
              <div className="text-xs text-text-muted flex items-center gap-1" data-id="element-262">
                {comparisonData?.trend === 'up' ? '▲' : '▼'}{' '}
                {comparisonData?.diff}{' '}
                {comparisonData?.trend === 'up' ? 'more' : 'less'}
              </div>
            </div> : <div className="p-4 bg-white/5 rounded-lg" data-id="element-263">
              <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-264">
                Vs Last Week
              </div>
              <div className={`text-2xl font-bold ${currentMetricData.lastWeek.trend === 'up' ? 'text-accent-success' : 'text-accent-error'}`} data-id="element-265">
                {currentMetricData.lastWeek.change}
              </div>
              <div className="text-xs text-text-muted" data-id="element-266">
                {currentMetricData.lastWeek.value} LW
              </div>
            </div>}

          {/* Card 4: 14-Day Avg (always shown) */}
          <div className="p-4 bg-white/5 rounded-lg" data-id="element-267">
            <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-268">
              14-Day Avg
            </div>
            <div className="text-2xl font-bold" data-id="element-269">
              {currentMetricData.avg14Day}
            </div>
            <div className="text-xs text-text-muted" data-id="element-270">average</div>
          </div>
        </div>

        {/* Comparison Toggle */}
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-id="element-271">
          <span className="text-sm text-text-secondary mr-2" data-id="element-272">
            Compare with:
          </span>
          {[{
          key: 'yesterday' as const,
          label: 'Yesterday'
        }, {
          key: 'lastWeek' as const,
          label: 'Last Week'
        }, {
          key: 'lastMonth' as const,
          label: 'Last Month'
        }, {
          key: 'lastYear' as const,
          label: 'Last Year'
        }].map(option => <button key={option.key} onClick={() => setComparison(comparison === option.key ? null : option.key)} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border ${comparison === option.key ? 'bg-accent-primary text-white border-accent-primary' : 'bg-transparent text-text-secondary border-white/10 hover:border-white/20 hover:text-white'}`} data-id="element-273">
              {option.label}
            </button>)}
        </div>

        <div className="h-[300px] w-full" data-id="element-274">
          <ResponsiveContainer width="100%" height="100%" data-id="element-275">
            <LineChart data={currentMetricData.chartData} data-id="element-276">
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-277" />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} dy={10} data-id="element-278" />
              <YAxis stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => metricConfig.format(value)} data-id="element-279" />
              <Tooltip contentStyle={{
              backgroundColor: '#1A1F2E',
              borderColor: '#ffffff10',
              borderRadius: '8px'
            }} itemStyle={{
              color: '#fff'
            }} formatter={(value: any) => metricConfig.format(value)} data-id="element-280" />
              <Legend wrapperStyle={{
              paddingTop: '20px'
            }} iconType="line" formatter={value => <span className="text-sm text-text-secondary" data-id="element-282">{value}</span>} data-id="element-281" />
              <Line type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} dot={{
              fill: '#8B5CF6',
              r: 4
            }} name="Current" data-id="element-283" />
              {comparison && <Line type="monotone" dataKey={getChartComparisonKey()!} stroke="#6B7280" strokeWidth={2} strokeDasharray="5 5" dot={{
              fill: '#6B7280',
              r: 3
            }} name={comparison === 'yesterday' ? 'Yesterday' : comparison === 'lastWeek' ? 'Last Week' : comparison === 'lastMonth' ? 'Last Month' : 'Last Year'} data-id="element-284" />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Widget Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-285">
        {/* Channel Breakdown */}
        <Card data-id="element-286">
          <div className="flex items-center gap-3 mb-6" data-id="element-287">
            <div className="p-2 bg-white/5 rounded-lg" data-id="element-288">
              <BarChart className="text-blue-400" data-id="element-289" />
            </div>
            <h2 className="text-lg font-bold" data-id="element-290">Channel Revenue Breakdown</h2>
          </div>

          <div className="space-y-4" data-id="element-291">
            {currentPeriodData.channels.map(channel => <div key={channel.name} data-id="element-292">
                <div className="flex items-center justify-between mb-2" data-id="element-293">
                  <div className="flex items-center gap-3" data-id="element-294">
                    <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: channel.color
                }} data-id="element-295"></div>
                    <span className="font-medium" data-id="element-296">{channel.name}</span>
                    <span className="text-xs text-text-secondary" data-id="element-297">
                      ({channel.percent}%)
                    </span>
                  </div>
                  <div className="text-right" data-id="element-298">
                    <div className="font-bold" data-id="element-299">{channel.revenue}K</div>
                    <div className="text-xs text-text-secondary" data-id="element-300">
                      Net Revenue
                    </div>
                  </div>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-301">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${channel.percent}%`,
                backgroundColor: channel.color
              }} data-id="element-302"></div>
                </div>
              </div>)}

            <div className="mt-6 p-3 bg-accent-success/10 border border-accent-success/20 rounded-lg flex items-start gap-3" data-id="element-303">
              <Lightbulb className="w-5 h-5 text-accent-success shrink-0 mt-0.5" data-id="element-304" />
              <p className="text-sm text-accent-success" data-id="element-305">
                Moving 10% of orders from Wolt → Collina.rs saves{' '}
                <span className="font-bold" data-id="element-306">~257K RSD/month</span> in fees
              </p>
            </div>
          </div>
        </Card>

        {/* Location Breakdown */}
        <Card data-id="element-307">
          <div className="flex items-center justify-between mb-6" data-id="element-308">
            <div className="flex items-center gap-3" data-id="element-309">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-310">
                <Package className="text-accent-error" data-id="element-311" />
              </div>
              <h2 className="text-lg font-bold" data-id="element-312">
                Revenue & Orders by Location
              </h2>
            </div>
          </div>

          <div className="space-y-6" data-id="element-313">
            {currentPeriodData.locations.map(loc => <div key={loc.name} data-id="element-314">
                <div className="flex justify-between items-center mb-2" data-id="element-315">
                  <div className="flex items-center gap-2" data-id="element-316">
                    <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: loc.color
                }} data-id="element-317"></div>
                    <span className="font-medium text-sm" data-id="element-318">{loc.name}</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-319">
                    <span className="font-bold" data-id="element-320">{loc.revenue / 1000}K</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${loc.growth > 0 ? 'bg-accent-success/20 text-accent-success' : 'bg-accent-error/20 text-accent-error'}`} data-id="element-321">
                      {loc.growth > 0 ? '↗' : '↘'} {Math.abs(loc.growth)}%
                    </span>
                    <span className="text-xs text-text-secondary w-12 text-right" data-id="element-322">
                      {loc.orders} ord
                    </span>
                  </div>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-323">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${loc.revenue / Math.max(...currentPeriodData.locations.map(l => l.revenue)) * 100}%`,
                backgroundColor: loc.color
              }} data-id="element-324"></div>
                </div>
              </div>)}
          </div>
        </Card>

        {/* Top Products */}
        <Card className="lg:col-span-2" data-id="element-325">
          <div className="flex items-center gap-3 mb-6" data-id="element-326">
            <div className="p-2 bg-white/5 rounded-lg" data-id="element-327">
              <Star className="text-accent-warning" data-id="element-328" />
            </div>
            <h2 className="text-lg font-bold" data-id="element-329">Top Products</h2>
          </div>

          <div className="overflow-x-auto" data-id="element-330">
            <table className="w-full text-sm" data-id="element-331">
              <thead data-id="element-332">
                <tr className="text-text-secondary border-b border-white/5" data-id="element-333">
                  <th className="text-left py-3 pl-4 font-medium" data-id="element-334">#</th>
                  <th className="text-left py-3 font-medium" data-id="element-335">PRODUCT</th>
                  <th className="text-right py-3 font-medium" data-id="element-336">RATING</th>
                  <th className="text-right py-3 font-medium" data-id="element-337">REVENUE</th>
                  <th className="text-right py-3 font-medium" data-id="element-338">PROFIT</th>
                  <th className="text-right py-3 pr-4 font-medium" data-id="element-339">MARGIN</th>
                </tr>
              </thead>
              <tbody data-id="element-340">
                {topProducts.map(product => <tr key={product.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-341">
                    <td className="py-4 pl-4" data-id="element-342">
                      <div className="w-6 h-6 rounded bg-accent-primary/20 text-accent-primary flex items-center justify-center text-xs font-bold" data-id="element-343">
                        {product.id}
                      </div>
                    </td>
                    <td className="py-4 font-medium" data-id="element-344">
                      <div data-id="element-345">{product.name}</div>
                      <div className="text-xs text-text-secondary mt-1" data-id="element-346">
                        {product.sold} sold
                      </div>
                    </td>
                    <td className="py-4 text-right" data-id="element-347">
                      <div className="flex items-center justify-end gap-1 font-bold text-accent-success" data-id="element-348">
                        {product.rating} <Star size={12} fill="currentColor" data-id="element-349" />
                      </div>
                    </td>
                    <td className="py-4 text-right font-bold" data-id="element-350">
                      {product.revenue}
                    </td>
                    <td className="py-4 text-right font-bold text-accent-success" data-id="element-351">
                      {product.profit}
                    </td>
                    <td className="py-4 text-right pr-4 font-bold text-accent-success" data-id="element-352">
                      {product.margin}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Profit Breakdown */}
        <Card className="lg:col-span-2" data-id="element-353">
          <div className="flex items-center gap-3 mb-6" data-id="element-354">
            <div className="p-2 bg-white/5 rounded-lg" data-id="element-355">
              <DollarSign className="text-accent-success" data-id="element-356" />
            </div>
            <h2 className="text-lg font-bold" data-id="element-357">Profit Breakdown</h2>
          </div>

          <div className="space-y-1" data-id="element-358">
            {pnlData.map((item, i) => <div key={i} className={`flex items-center justify-between py-3 px-4 rounded-lg ${item.isTotal ? 'bg-white/5 font-bold' : 'hover:bg-white/5 transition-colors'}`} data-id="element-359">
                <div className="flex items-center gap-3" data-id="element-360">
                  <span className={item.isTotal ? 'text-white' : 'text-text-secondary'} data-id="element-361">
                    {item.label}
                  </span>
                </div>
                <div className="flex items-center gap-8" data-id="element-362">
                  <span className={`w-24 text-right ${item.color}`} data-id="element-363">
                    {item.value}
                  </span>
                  <span className="w-12 text-right text-text-secondary text-sm" data-id="element-364">
                    {item.percent}
                  </span>
                </div>
              </div>)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6" data-id="element-365">
            <div className="p-4 bg-white/5 rounded-lg text-center" data-id="element-366">
              <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-367">
                BRUTTO
              </div>
              <div className="text-2xl font-bold text-accent-primary" data-id="element-368">780K</div>
            </div>
            <div className="p-4 bg-white/5 rounded-lg text-center" data-id="element-369">
              <div className="text-xs text-text-secondary uppercase mb-1" data-id="element-370">
                EBITDA
              </div>
              <div className="text-2xl font-bold text-accent-warning" data-id="element-371">425K</div>
              <div className="text-xs text-text-muted" data-id="element-372">55%</div>
            </div>
            <div className="p-4 bg-accent-success/10 border border-accent-success/20 rounded-lg text-center" data-id="element-373">
              <div className="text-xs text-accent-success uppercase mb-1" data-id="element-374">
                NET PROFIT
              </div>
              <div className="text-2xl font-bold text-accent-success" data-id="element-375">362K</div>
              <div className="text-xs text-accent-success/80" data-id="element-376">46.3%</div>
            </div>
          </div>
        </Card>

        {/* NEW SECTION 1: Hourly Analysis + Day of Week */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-377">
          <Card data-id="element-378">
            <div className="flex items-center gap-3 mb-6" data-id="element-379">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-380">
                <Clock className="text-accent-primary" data-id="element-381" />
              </div>
              <h2 className="text-lg font-bold" data-id="element-382">Hourly Analysis</h2>
            </div>
            <div className="h-[250px] w-full mb-6" data-id="element-383">
              <ResponsiveContainer width="100%" height="100%" data-id="element-384">
                <ComposedChart data={hourlyAnalysisData} data-id="element-385">
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-386" />
                  <XAxis dataKey="hour" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} dy={10} data-id="element-387" />
                  <YAxis yAxisId="left" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-388" />
                  <YAxis yAxisId="right" orientation="right" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-389" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1A1F2E',
                  borderColor: '#ffffff10',
                  borderRadius: '8px'
                }} itemStyle={{
                  color: '#fff'
                }} data-id="element-390" />
                  <Bar yAxisId="left" dataKey="orders" fill="#8B5CF6" radius={[4, 4, 0, 0]} barSize={20} name="Orders" data-id="element-391" />
                  <Line yAxisId="right" type="monotone" dataKey="prep" stroke="#22C55E" strokeWidth={2} dot={false} name="Prep Time" data-id="element-392" />
                  <Line yAxisId="right" type="monotone" dataKey="delivery" stroke="#3B82F6" strokeWidth={2} dot={false} name="Delivery Time" data-id="element-393" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3" data-id="element-394">
              <h3 className="text-sm font-bold text-text-secondary uppercase mb-2" data-id="element-395">
                Critical Hours
              </h3>
              {criticalHours.map((hour, i) => <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg text-sm" data-id="element-396">
                  <span className="font-bold w-16" data-id="element-397">{hour.hour}</span>
                  <span className="text-text-secondary" data-id="element-398">
                    {hour.orders} orders
                  </span>
                  <span className="font-medium" data-id="element-399">{hour.revenue}</span>
                  <span className="text-blue-400" data-id="element-400">
                    {hour.delivery} delivery
                  </span>
                  <span className="text-accent-success flex items-center gap-1" data-id="element-401">
                    {hour.rating} <Star size={10} fill="currentColor" data-id="element-402" />
                  </span>
                </div>)}
            </div>
          </Card>

          <Card data-id="element-403">
            <div className="flex items-center gap-3 mb-6" data-id="element-404">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-405">
                <Calendar className="text-accent-primary" data-id="element-406" />
              </div>
              <h2 className="text-lg font-bold" data-id="element-407">Day of Week</h2>
            </div>
            <div className="space-y-4" data-id="element-408">
              {dayOfWeekData.map(day => <div key={day.day} className="relative" data-id="element-409">
                  <div className="flex justify-between items-center mb-1 text-sm" data-id="element-410">
                    <span className={`font-bold w-12 ${day.isToday ? 'text-accent-primary' : 'text-text-secondary'}`} data-id="element-411">
                      {day.day}
                    </span>
                    <div className="flex-1 mx-4 h-[1px] bg-white/10 relative top-1" data-id="element-412"></div>
                    <div className="flex gap-4" data-id="element-413">
                      <span className="font-bold w-12 text-right" data-id="element-414">
                        {day.revenue}K
                      </span>
                      <span className="text-text-secondary w-16 text-right" data-id="element-415">
                        {day.orders} ord
                      </span>
                    </div>
                  </div>
                  <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden flex" data-id="element-416">
                    <div className={`h-full rounded-full ${day.isToday ? 'bg-accent-primary' : 'bg-accent-success'}`} style={{
                  width: `${day.revenue / 1000 * 100}%`
                }} data-id="element-417"></div>
                  </div>
                  {day.isToday && <span className="absolute -top-1 left-14 bg-accent-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-418">
                      TODAY
                    </span>}
                </div>)}
            </div>
          </Card>
        </div>

        {/* NEW SECTION 2: Kitchen & Delivery Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-419">
          <Card data-id="element-420">
            <div className="flex items-center justify-between mb-6" data-id="element-421">
              <div className="flex items-center gap-3" data-id="element-422">
                <div className="p-2 bg-white/5 rounded-lg" data-id="element-423">
                  <Zap className="text-accent-warning" data-id="element-424" />
                </div>
                <h2 className="text-lg font-bold" data-id="element-425">Kitchen Performance</h2>
              </div>
              <div className="flex gap-2" data-id="element-426">
                <span className="bg-accent-warning/20 text-accent-warning text-xs font-bold px-2 py-1 rounded" data-id="element-427">
                  [SIM]
                </span>
                <AlertTriangle size={16} className="text-accent-warning" data-id="element-428" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-429">
              <div className="space-y-3" data-id="element-430">
                <h3 className="text-xs font-bold text-text-secondary uppercase mb-2" data-id="element-431">
                  Prep Time by Venue
                </h3>
                {prepTimeByVenue.map((venue, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-432">
                    <div className="flex items-center gap-2" data-id="element-433">
                      <Check size={14} className="text-accent-success" data-id="element-434" />
                      <span data-id="element-435">{venue.name}</span>
                    </div>
                    <div className="flex gap-3" data-id="element-436">
                      <span className="font-bold" data-id="element-437">{venue.time}</span>
                      <span className="text-text-secondary w-8 text-right" data-id="element-438">
                        {venue.orders}
                      </span>
                    </div>
                  </div>)}
              </div>

              <div data-id="element-439">
                <h3 className="text-xs font-bold text-text-secondary uppercase mb-2" data-id="element-440">
                  Prep Time by Hour
                </h3>
                <div className="h-[200px] w-full" data-id="element-441">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-442">
                    <BarChart data={prepTimeByHour} layout="vertical" barSize={12} data-id="element-443">
                      <XAxis type="number" hide data-id="element-444" />
                      <YAxis dataKey="hour" type="category" stroke="#6B7280" fontSize={10} tickLine={false} axisLine={false} width={30} data-id="element-445" />
                      <Tooltip cursor={{
                      fill: 'transparent'
                    }} contentStyle={{
                      backgroundColor: '#1A1F2E',
                      borderColor: '#ffffff10'
                    }} data-id="element-446" />
                      <Bar dataKey="good" stackId="a" fill="#22C55E" radius={[0, 4, 4, 0]} data-id="element-447" />
                      <Bar dataKey="warn" stackId="a" fill="#F59E0B" data-id="element-448" />
                      <Bar dataKey="crit" stackId="a" fill="#EF4444" data-id="element-449" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </Card>

          <Card data-id="element-450">
            <div className="flex items-center justify-between mb-6" data-id="element-451">
              <div className="flex items-center gap-3" data-id="element-452">
                <div className="p-2 bg-white/5 rounded-lg" data-id="element-453">
                  <Clock className="text-blue-400" data-id="element-454" />
                </div>
                <h2 className="text-lg font-bold" data-id="element-455">Delivery Performance</h2>
              </div>
              <AlertTriangle size={16} className="text-accent-warning" data-id="element-456" />
            </div>

            <div className="mb-6" data-id="element-457">
              <h3 className="text-xs font-bold text-text-secondary uppercase mb-2" data-id="element-458">
                Time Breakdown (avg)
              </h3>
              <div className="flex text-xs font-bold mb-1 justify-between px-1" data-id="element-459">
                <span data-id="element-460">Order</span>
                <span data-id="element-461">Delivered</span>
              </div>
              <div className="h-4 w-full flex rounded-full overflow-hidden mb-1" data-id="element-462">
                <div className="bg-orange-400 w-[30%]" data-id="element-463"></div>
                <div className="bg-blue-400 w-[15%]" data-id="element-464"></div>
                <div className="bg-green-500 w-[55%]" data-id="element-465"></div>
              </div>
              <div className="flex text-[10px] text-text-secondary justify-between px-1" data-id="element-466">
                <span className="text-orange-400" data-id="element-467">Prep 13m</span>
                <span className="text-blue-400" data-id="element-468">Pickup 6m</span>
                <span className="text-green-500" data-id="element-469">Drive 22m</span>
              </div>
            </div>

            <div className="space-y-3" data-id="element-470">
              <h3 className="text-xs font-bold text-text-secondary uppercase mb-2" data-id="element-471">
                Delivery by Venue
              </h3>
              {deliveryByVenue.map((venue, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-472">
                  <span className="w-28 truncate" data-id="element-473">{venue.name}</span>
                  <div className="flex-1 mx-3 h-2 bg-white/5 rounded-full overflow-hidden" data-id="element-474">
                    <div className={`h-full rounded-full ${venue.status === 'crit' ? 'bg-accent-error' : 'bg-orange-400'}`} style={{
                  width: `${venue.percent}%`
                }} data-id="element-475"></div>
                  </div>
                  <span className="font-bold w-12" data-id="element-476">{venue.time}</span>
                  <span className="text-text-secondary text-xs w-16 text-right" data-id="element-477">
                    {venue.percent}% &lt;35m
                  </span>
                  {venue.status === 'crit' && <AlertTriangle size={12} className="text-accent-error ml-1" data-id="element-478" />}
                </div>)}
            </div>
          </Card>
        </div>

        {/* NEW SECTION 3: Category Performance */}
        <Card data-id="element-479">
          <div className="flex items-center gap-3 mb-6" data-id="element-480">
            <div className="p-2 bg-white/5 rounded-lg" data-id="element-481">
              <Package className="text-accent-primary" data-id="element-482" />
            </div>
            <h2 className="text-lg font-bold" data-id="element-483">Category Performance</h2>
            <div className="ml-auto flex gap-4 text-xs font-bold" data-id="element-484">
              <div className="flex items-center gap-2" data-id="element-485">
                <div className="w-3 h-3 bg-accent-primary rounded" data-id="element-486"></div>{' '}
                Revenue
              </div>
              <div className="flex items-center gap-2" data-id="element-487">
                <div className="w-3 h-3 bg-accent-success rounded" data-id="element-488"></div> Margin
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-489">
            {categoryPerformance.map(cat => <div key={cat.name} className="grid grid-cols-[120px_1fr_150px] gap-4 items-center" data-id="element-490">
                <div data-id="element-491">
                  <div className="font-bold" data-id="element-492">{cat.name}</div>
                  <div className="text-xs text-text-secondary" data-id="element-493">
                    {cat.percent}% of sales
                  </div>
                </div>
                <div className="space-y-1" data-id="element-494">
                  <div className="h-3 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-495">
                    <div className="h-full bg-accent-primary rounded-full" style={{
                  width: `${cat.percent}%`
                }} data-id="element-496"></div>
                  </div>
                  <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden opacity-60" data-id="element-497">
                    <div className="h-full bg-accent-success rounded-full" style={{
                  width: `${cat.margin}%`
                }} data-id="element-498"></div>
                  </div>
                </div>
                <div className="text-right" data-id="element-499">
                  <div className="font-bold" data-id="element-500">{cat.revenue}</div>
                  <div className="text-xs text-accent-success" data-id="element-501">
                    {cat.margin}% margin
                  </div>
                </div>
              </div>)}
          </div>
        </Card>

        {/* NEW SECTION 4: Customer Insights + Reviews */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-502">
          <Card data-id="element-503">
            <div className="flex items-center gap-3 mb-6" data-id="element-504">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-505">
                <Users className="text-accent-primary" data-id="element-506" />
              </div>
              <h2 className="text-lg font-bold" data-id="element-507">Customer Insights</h2>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-508">
              <div className="p-3 bg-white/5 rounded-lg" data-id="element-509">
                <div className="text-xs text-text-secondary uppercase" data-id="element-510">
                  Total Customers
                </div>
                <div className="text-xl font-bold" data-id="element-511">444</div>
              </div>
              <div className="p-3 bg-white/5 rounded-lg" data-id="element-512">
                <div className="text-xs text-text-secondary uppercase" data-id="element-513">
                  Repeat Rate
                </div>
                <div className="text-xl font-bold" data-id="element-514">68%</div>
              </div>
              <div className="p-3 bg-white/5 rounded-lg" data-id="element-515">
                <div className="text-xs text-text-secondary uppercase" data-id="element-516">
                  New Customers
                </div>
                <div className="text-xl font-bold" data-id="element-517">142</div>
                <div className="text-xs text-text-secondary" data-id="element-518">
                  32.0% of total
                </div>
              </div>
              <div className="p-3 bg-white/5 rounded-lg" data-id="element-519">
                <div className="text-xs text-text-secondary uppercase" data-id="element-520">
                  Returning
                </div>
                <div className="text-xl font-bold" data-id="element-521">302</div>
                <div className="text-xs text-text-secondary" data-id="element-522">
                  68.0% of total
                </div>
              </div>
            </div>

            <div className="mb-6" data-id="element-523">
              <h3 className="text-xs font-bold text-text-secondary uppercase mb-3" data-id="element-524">
                Average Basket Comparison
              </h3>
              <div className="flex items-center gap-4" data-id="element-525">
                <div className="flex-1 p-3 bg-white/5 rounded-lg border-l-2 border-accent-primary" data-id="element-526">
                  <div className="text-xs text-text-secondary" data-id="element-527">
                    New Customers
                  </div>
                  <div className="text-lg font-bold" data-id="element-528">1,641 RSD</div>
                </div>
                <div className="flex-1 p-3 bg-white/5 rounded-lg border-l-2 border-accent-success" data-id="element-529">
                  <div className="text-xs text-text-secondary" data-id="element-530">Returning</div>
                  <div className="text-lg font-bold" data-id="element-531">1,417 RSD</div>
                </div>
              </div>
            </div>

            <div data-id="element-532">
              <h3 className="text-xs font-bold text-text-secondary uppercase mb-3" data-id="element-533">
                Top VIP Customers (14 days)
              </h3>
              <div className="space-y-2" data-id="element-534">
                {vipCustomers.map(vip => <div key={vip.id} className="flex items-center justify-between p-2 bg-white/5 rounded hover:bg-white/10 transition-colors text-sm" data-id="element-535">
                    <div className="flex items-center gap-3" data-id="element-536">
                      <span className="w-5 h-5 rounded-full bg-accent-primary/20 text-accent-primary flex items-center justify-center text-xs font-bold" data-id="element-537">
                        {vip.rank}
                      </span>
                      <span className="font-medium" data-id="element-538">{vip.id}</span>
                    </div>
                    <div className="flex gap-4" data-id="element-539">
                      <span className="text-text-secondary" data-id="element-540">
                        {vip.orders} orders
                      </span>
                      <span className="font-bold w-10 text-right" data-id="element-541">
                        {vip.spent}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </Card>

          <Card data-id="element-542">
            <div className="flex items-center justify-between mb-6" data-id="element-543">
              <div className="flex items-center gap-3" data-id="element-544">
                <div className="p-2 bg-white/5 rounded-lg" data-id="element-545">
                  <Star className="text-accent-warning" data-id="element-546" />
                </div>
                <h2 className="text-lg font-bold" data-id="element-547">Reviews</h2>
              </div>
              <span className="text-sm text-text-secondary" data-id="element-548">62 reviews</span>
            </div>

            <div className="mb-6" data-id="element-549">
              <h3 className="text-xs font-bold text-text-secondary uppercase mb-3" data-id="element-550">
                Rating Distribution
              </h3>
              <div className="space-y-2" data-id="element-551">
                {ratingDist.map(rating => <div key={rating.label} className="flex items-center gap-3 text-sm" data-id="element-552">
                    <span className="w-10 font-medium" data-id="element-553">{rating.label}</span>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden" data-id="element-554">
                      <div className={`h-full ${rating.color}`} style={{
                    width: `${rating.count / 62 * 100}%`
                  }} data-id="element-555"></div>
                    </div>
                    <span className="w-6 text-right text-text-secondary" data-id="element-556">
                      {rating.count}
                    </span>
                  </div>)}
              </div>
            </div>

            <div className="space-y-3" data-id="element-557">
              <h3 className="text-xs font-bold text-text-secondary uppercase mb-2" data-id="element-558">
                Recent Negative Reviews
              </h3>

              <div className="p-3 bg-white/5 rounded-lg border-l-2 border-accent-error" data-id="element-559">
                <div className="flex justify-between text-xs mb-1" data-id="element-560">
                  <span className="font-bold text-accent-error" data-id="element-561">
                    [8/10] Novi Beograd Burgers
                  </span>
                  <span className="text-text-secondary" data-id="element-562">14:23</span>
                </div>
                <p className="text-sm" data-id="element-563">"Burger suv, potrebna poboljšanja"</p>
              </div>

              <div className="p-3 bg-white/5 rounded-lg border-l-2 border-accent-error" data-id="element-564">
                <div className="flex justify-between text-xs mb-1" data-id="element-565">
                  <span className="font-bold text-accent-error" data-id="element-566">
                    [7/10] Vračar Burgers
                  </span>
                  <span className="text-text-secondary" data-id="element-567">19:45</span>
                </div>
                <p className="text-sm" data-id="element-568">"Dugo čekanje"</p>
              </div>

              <div className="p-3 bg-accent-warning/10 border border-accent-warning/20 rounded-lg flex gap-3 mt-4" data-id="element-569">
                <AlertTriangle className="w-5 h-5 text-accent-warning shrink-0" data-id="element-570" />
                <div className="text-sm text-accent-warning" data-id="element-571">
                  <span className="font-bold" data-id="element-572">Alert:</span> 2 of 5 negatives
                  from Novi Beograd Burgers - Check burger quality
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* NEW SECTION 5: Key Insights + Recommended Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-573">
          <Card data-id="element-574">
            <div className="flex items-center gap-3 mb-6" data-id="element-575">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-576">
                <Lightbulb className="text-accent-primary" data-id="element-577" />
              </div>
              <h2 className="text-lg font-bold" data-id="element-578">KEY INSIGHTS</h2>
            </div>
            <div className="space-y-4" data-id="element-579">
              {keyInsights.map((insight, i) => <div key={i} className="flex gap-4 p-3 bg-white/5 rounded-lg" data-id="element-580">
                  <div className="p-2 bg-background-elevated rounded-lg h-fit" data-id="element-581">
                    <insight.icon size={18} className="text-accent-primary" data-id="element-582" />
                  </div>
                  <div data-id="element-583">
                    <div className="font-bold text-sm mb-1" data-id="element-584">
                      {insight.label}
                    </div>
                    <div className="text-sm text-text-secondary" data-id="element-585">
                      {insight.text}
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>

          <Card data-id="element-586">
            <div className="flex items-center gap-3 mb-6" data-id="element-587">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-588">
                <Check className="text-accent-success" data-id="element-589" />
              </div>
              <h2 className="text-lg font-bold" data-id="element-590">RECOMMENDED ACTIONS</h2>
            </div>
            <div className="space-y-3" data-id="element-591">
              {recommendedActions.map((action, i) => <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer group" data-id="element-592">
                  <div className="flex items-center gap-3" data-id="element-593">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${action.color}`} data-id="element-594">
                      {action.priority}
                    </span>
                    <div data-id="element-595">
                      <div className="text-sm font-medium group-hover:text-accent-primary transition-colors" data-id="element-596">
                        {action.action}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-597">
                        {action.detail}
                      </div>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-598" />
                </div>)}
            </div>
          </Card>
        </div>

        {/* NEW SECTION 6: Tomorrow's Forecast */}
        <Card className="border-t-4 border-t-accent-primary" data-id="element-599">
          <div className="flex items-center justify-between mb-8" data-id="element-600">
            <div className="flex items-center gap-3" data-id="element-601">
              <div className="p-2 bg-accent-primary/20 rounded-lg" data-id="element-602">
                <Zap className="text-accent-primary" data-id="element-603" />
              </div>
              <div data-id="element-604">
                <h2 className="text-lg font-bold" data-id="element-605">Tomorrow's Forecast</h2>
                <p className="text-xs text-text-secondary" data-id="element-606">Sunday, Dec 14</p>
              </div>
            </div>
            <span className="bg-accent-success/20 text-accent-success text-xs font-bold px-3 py-1 rounded-full" data-id="element-607">
              HIGH CONFIDENCE
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-608">
            <div className="p-4 bg-white/5 rounded-lg border border-white/5" data-id="element-609">
              <div className="text-xs text-text-secondary uppercase mb-2" data-id="element-610">
                EXPECTED REVENUE
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-611">
                <span className="text-2xl font-bold" data-id="element-612">715K</span>
                <span className="text-sm text-text-secondary" data-id="element-613">RSD</span>
              </div>
              <div className="text-xs text-accent-error flex items-center gap-1 mb-2" data-id="element-614">
                <TrendingDown size={12} data-id="element-615" /> 8.4% vs today
              </div>
              <div className="text-[10px] text-text-secondary bg-white/5 px-2 py-1 rounded inline-block" data-id="element-616">
                Based on Mon historical avg
              </div>
            </div>

            <div className="p-4 bg-white/5 rounded-lg border border-white/5" data-id="element-617">
              <div className="text-xs text-text-secondary uppercase mb-2" data-id="element-618">
                EXPECTED ORDERS
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-619">
                <span className="text-2xl font-bold" data-id="element-620">483</span>
                <span className="text-sm text-text-secondary" data-id="element-621">orders</span>
              </div>
              <div className="text-xs text-accent-error flex items-center gap-1 mb-2" data-id="element-622">
                <TrendingDown size={12} data-id="element-623" /> 7.6% vs today
              </div>
              <div className="text-[10px] text-text-secondary bg-white/5 px-2 py-1 rounded inline-block" data-id="element-624">
                Prepare kitchen capacity
              </div>
            </div>

            <div className="p-4 bg-white/5 rounded-lg border border-white/5" data-id="element-625">
              <div className="text-xs text-text-secondary uppercase mb-2" data-id="element-626">
                AVG BASKET
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-627">
                <span className="text-2xl font-bold" data-id="element-628">1,480</span>
                <span className="text-sm text-text-secondary" data-id="element-629">RSD</span>
              </div>
              <div className="text-xs text-text-secondary flex items-center gap-1 mb-2" data-id="element-630">
                <ArrowRight size={12} data-id="element-631" /> 0% vs today
              </div>
              <div className="text-[10px] text-text-secondary bg-white/5 px-2 py-1 rounded inline-block" data-id="element-632">
                Focus on upsell sides
              </div>
            </div>

            <div className="p-4 bg-white/5 rounded-lg border border-white/5" data-id="element-633">
              <div className="text-xs text-text-secondary uppercase mb-2" data-id="element-634">
                TARGET RATING
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-635">
                <span className="text-2xl font-bold text-accent-primary" data-id="element-636">
                  9.0+
                </span>
                <span className="text-sm text-text-secondary" data-id="element-637">/ 10</span>
              </div>
              <div className="text-xs text-accent-primary font-bold mb-2" data-id="element-638">
                TARGET GOAL
              </div>
              <div className="text-[10px] text-text-secondary bg-white/5 px-2 py-1 rounded inline-block" data-id="element-639">
                Maintain quality standards
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-640">
            <div data-id="element-641">
              <h3 className="text-sm font-bold text-text-secondary uppercase mb-4" data-id="element-642">
                Peak Hours Expected
              </h3>
              <div className="space-y-3" data-id="element-643">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-id="element-644">
                  <span className="font-bold w-24" data-id="element-645">12:00-14:00</span>
                  <span className="text-accent-primary font-medium" data-id="element-646">
                    Lunch Rush
                  </span>
                  <span className="text-text-secondary" data-id="element-647">~80 orders</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-id="element-648">
                  <span className="font-bold w-24" data-id="element-649">19:00-21:00</span>
                  <span className="text-accent-primary font-medium" data-id="element-650">
                    Dinner Peak
                  </span>
                  <span className="text-text-secondary" data-id="element-651">~110 orders</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-id="element-652">
                  <span className="font-bold w-24" data-id="element-653">22:00-23:00</span>
                  <span className="text-accent-primary font-medium" data-id="element-654">
                    Late Night
                  </span>
                  <span className="text-text-secondary" data-id="element-655">~50 orders</span>
                </div>
              </div>
            </div>

            <div data-id="element-656">
              <h3 className="text-sm font-bold text-text-secondary uppercase mb-4" data-id="element-657">
                Recommendations for Tomorrow
              </h3>
              <div className="space-y-3" data-id="element-658">
                <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg" data-id="element-659">
                  <div className="text-lg" data-id="element-660">🍔</div>
                  <div className="flex-1" data-id="element-661">
                    <div className="text-sm font-medium" data-id="element-662">
                      Sunday typically similar to Saturday
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-663">
                      Maintain staffing levels
                    </div>
                  </div>
                  <span className="text-[10px] font-bold bg-accent-warning/20 text-accent-warning px-2 py-1 rounded" data-id="element-664">
                    MEDIUM
                  </span>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg" data-id="element-665">
                  <div className="text-lg" data-id="element-666">⭐</div>
                  <div className="flex-1" data-id="element-667">
                    <div className="text-sm font-medium" data-id="element-668">
                      Great rating today (9.3/10)!
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-669">
                      Keep quality consistent
                    </div>
                  </div>
                  <span className="text-[10px] font-bold bg-accent-error/20 text-accent-error px-2 py-1 rounded" data-id="element-670">
                    HIGH
                  </span>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg" data-id="element-671">
                  <div className="text-lg" data-id="element-672">📅</div>
                  <div className="flex-1" data-id="element-673">
                    <div className="text-sm font-medium" data-id="element-674">
                      Week ending strong
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-675">
                      Prep for Monday reset
                    </div>
                  </div>
                  <span className="text-[10px] font-bold bg-accent-success/20 text-accent-success px-2 py-1 rounded" data-id="element-676">
                    LOW
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>;
}